import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Navigation } from '@/components/layout/Navigation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Calendar,
  MapPin,
  Users,
  Store,
  Plus,
  ArrowRight,
  Loader2,
  Ticket,
  QrCode,
  Palette
} from 'lucide-react';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FestivalDashboard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [festivals, setFestivals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  
  const [newFestival, setNewFestival] = useState({
    name: '',
    description: '',
    venue: '',
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    fetchFestivals();
  }, []);

  const fetchFestivals = async () => {
    try {
      const response = await axios.get(`${API}/festival/my-festivals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFestivals(response.data);
    } catch (error) {
      console.error('Failed to fetch festivals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFestival = async (e) => {
    e.preventDefault();
    setCreating(true);
    
    try {
      const response = await axios.post(`${API}/festival/create`, newFestival, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Festival created successfully!');
      setFestivals([response.data, ...festivals]);
      setCreateDialogOpen(false);
      setNewFestival({ name: '', description: '', venue: '', start_date: '', end_date: '' });
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Festival Organizer subscription required');
        navigate('/subscription');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to create festival');
      }
    } finally {
      setCreating(false);
    }
  };

  const hasFestivalSubscription = user?.subscription_plan === 'festival' || user?.subscription_plan === 'enterprise';

  return (
    <div className="min-h-screen bg-background" data-testid="festival-dashboard">
      <Navigation />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">
              Festival <span className="text-[#5DBDB3]">Wristbands</span>
            </h1>
            <p className="text-muted-foreground text-lg">
              Manage allergen wristbands for your festival events
            </p>
          </div>
          
          {hasFestivalSubscription ? (
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#5DBDB3] hover:bg-[#4DA9A0]" data-testid="create-festival-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Festival
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                  <DialogTitle>Create New Festival</DialogTitle>
                  <DialogDescription>
                    Set up a new festival event with wristband management
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateFestival} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Festival Name *</Label>
                    <Input
                      id="name"
                      placeholder="Summer Music Festival 2025"
                      value={newFestival.name}
                      onChange={(e) => setNewFestival({ ...newFestival, name: e.target.value })}
                      required
                      data-testid="festival-name-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="venue">Venue *</Label>
                    <Input
                      id="venue"
                      placeholder="Central Park, NYC"
                      value={newFestival.venue}
                      onChange={(e) => setNewFestival({ ...newFestival, venue: e.target.value })}
                      required
                      data-testid="festival-venue-input"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="start_date">Start Date *</Label>
                      <Input
                        id="start_date"
                        type="date"
                        value={newFestival.start_date}
                        onChange={(e) => setNewFestival({ ...newFestival, start_date: e.target.value })}
                        required
                        data-testid="festival-start-date"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="end_date">End Date *</Label>
                      <Input
                        id="end_date"
                        type="date"
                        value={newFestival.end_date}
                        onChange={(e) => setNewFestival({ ...newFestival, end_date: e.target.value })}
                        required
                        data-testid="festival-end-date"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      placeholder="A brief description of your festival..."
                      value={newFestival.description}
                      onChange={(e) => setNewFestival({ ...newFestival, description: e.target.value })}
                      data-testid="festival-description"
                    />
                  </div>
                  
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      className="bg-[#5DBDB3] hover:bg-[#4DA9A0]"
                      disabled={creating}
                      data-testid="submit-festival-btn"
                    >
                      {creating ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        'Create Festival'
                      )}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          ) : (
            <Link to="/subscription">
              <Button className="bg-[#F08B7D] hover:bg-[#E07A6C]">
                <Ticket className="w-4 h-4 mr-2" />
                Get Festival Plan
              </Button>
            </Link>
          )}
        </div>

        {/* Subscription CTA if not subscribed */}
        {!hasFestivalSubscription && (
          <Card className="mb-8 border-2 border-[#5DBDB3]/30 bg-[#5DBDB3]/5">
            <CardContent className="py-8">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="w-16 h-16 rounded-2xl bg-[#5DBDB3]/20 flex items-center justify-center">
                  <Ticket className="w-8 h-8 text-[#5DBDB3]" />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-xl font-semibold mb-2">Festival Organizer Plan</h3>
                  <p className="text-muted-foreground">
                    Create events, manage wristbands, set up vendor boards, and keep attendees safe with color-coded allergen wristbands.
                  </p>
                </div>
                <Link to="/subscription">
                  <Button size="lg" className="bg-[#5DBDB3] hover:bg-[#4DA9A0]" data-testid="get-festival-plan-btn">
                    Subscribe for £149/mo
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Features Overview */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          {[
            { icon: Palette, title: 'Color-Coded Bands', desc: 'Easy visual identification' },
            { icon: QrCode, title: 'QR Code Backup', desc: 'Scan when phone works' },
            { icon: Store, title: 'Vendor Boards', desc: 'Digital & printable displays' },
            { icon: Users, title: 'Bulk Registration', desc: 'Register many attendees' }
          ].map((item, index) => (
            <Card key={index} className="border border-border">
              <CardContent className="pt-6 text-center">
                <div className="w-10 h-10 rounded-lg bg-[#5DBDB3]/10 flex items-center justify-center mx-auto mb-3">
                  <item.icon className="w-5 h-5 text-[#5DBDB3]" />
                </div>
                <h3 className="font-medium text-sm">{item.title}</h3>
                <p className="text-xs text-muted-foreground mt-1">{item.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Festivals List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : festivals.length > 0 ? (
          <div className="space-y-4" data-testid="festivals-list">
            <h2 className="text-xl font-semibold">Your Festivals</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {festivals.map((festival) => (
                <Link key={festival.id} to={`/festival/${festival.id}`}>
                  <Card className="border border-border hover:border-[#5DBDB3]/50 transition-all duration-200 hover:-translate-y-1 cursor-pointer h-full">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="w-12 h-12 rounded-xl bg-[#5DBDB3]/10 flex items-center justify-center">
                          <Ticket className="w-6 h-6 text-[#5DBDB3]" />
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {festival.access_code}
                        </Badge>
                      </div>
                      
                      <h3 className="font-semibold text-lg mb-2">{festival.name}</h3>
                      
                      <div className="space-y-2 text-sm text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          {festival.venue}
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          {festival.start_date} - {festival.end_date}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4 mt-4 pt-4 border-t border-border">
                        <div className="flex items-center gap-1">
                          <Users className="w-4 h-4 text-[#5DBDB3]" />
                          <span className="text-sm font-medium">{festival.attendee_count}</span>
                          <span className="text-xs text-muted-foreground">attendees</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Store className="w-4 h-4 text-[#F08B7D]" />
                          <span className="text-sm font-medium">{festival.vendor_count}</span>
                          <span className="text-xs text-muted-foreground">vendors</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        ) : hasFestivalSubscription ? (
          <Card className="border border-dashed border-border">
            <CardContent className="py-12 text-center">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                <Ticket className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-2">No festivals yet</h3>
              <p className="text-muted-foreground mb-6">
                Create your first festival to start managing wristbands
              </p>
              <Button 
                onClick={() => setCreateDialogOpen(true)}
                className="bg-[#5DBDB3] hover:bg-[#4DA9A0]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Festival
              </Button>
            </CardContent>
          </Card>
        ) : null}

        {/* How It Works */}
        <div className="mt-16 p-8 bg-muted/50 rounded-2xl">
          <h2 className="text-2xl font-semibold mb-6 text-center">How Wristbands Work</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '1',
                title: 'Register Attendees',
                desc: 'Add attendees and their allergens. Each allergen gets a unique color.'
              },
              {
                step: '2',
                title: 'Print Wristbands',
                desc: 'Attendees wear color-coded wristbands matching their allergens + QR code.'
              },
              {
                step: '3',
                title: 'Vendor Displays',
                desc: 'Vendors show which colors can/cannot eat their food. Works without phones!'
              }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 rounded-full bg-[#5DBDB3] text-white font-bold text-lg flex items-center justify-center mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default FestivalDashboard;
